import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useRef, useState } from "react";
import i18n from "i18next";
import { IS_NATIVE_APP } from "../config/appTarget.js";
import { getClientHeaders } from "../lib/clientMeta.js";

/** Max time to wait for Auth0 to return an access token before treating the
 * request as failed. On native (Capacitor WKWebView) a broken/absent refresh
 * token can make getAccessTokenSilently hang on the silent-auth fallback until
 * Auth0's ~60s authorize timeout; capping it here fails fast instead. */
const TOKEN_TIMEOUT_SECONDS = 15;
/** Max time to wait for the network request itself (dead-connection guard). */
const REQUEST_TIMEOUT_MS = 20000;

function usesDevPermissionPrefix(): boolean {
  const isLocalDevAuth = String(
    import.meta.env.VITE_REACT_APP_API_URL || "",
  ).includes("localhost");
  return isLocalDevAuth || import.meta.env.VITE_APP_ENV === "qa";
}

/** Thrown when token acquisition or the request times out / has no connection.
 * Message is user-facing (Portuguese) so callers can surface it directly. */
export class NetworkTimeoutError extends Error {
  constructor(
    message = i18n.t("errors:network.timeout"),
  ) {
    super(message);
    this.name = "NetworkTimeoutError";
  }
}

/**
 * Custom hook to get authenticated API headers
 * Usage: const { getAuthHeaders, isLoading } = useAuthHeaders();
 */
export function useAuthHeaders() {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();

  const getAuthHeaders = async (): Promise<Record<string, string>> => {
    if (!isAuthenticated) {
      throw new Error("User is not authenticated");
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          scope: "openid profile email",
        },
      });

      return {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      };
    } catch (error) {
      console.error("Error getting access token:", error);
      throw new Error("Failed to get authentication token");
    }
  };

  return {
    getAuthHeaders,
    isLoading: isLoading || !isAuthenticated,
    isAuthenticated,
  };
}

/**
 * Helper function for authenticated API calls
 * Usage:
 * const response = await makeAuthenticatedRequest('/api/animals', {
 *   method: 'POST',
 *   body: JSON.stringify(animalData)
 * });
 */
export async function makeAuthenticatedRequest(
  url: string,
  options: RequestInit = {},
  getAccessTokenSilently: any,
): Promise<Response> {
  const isLocalDevAuth = String(
    import.meta.env.VITE_REACT_APP_API_URL || "",
  ).includes("localhost");
  try {
    if (isLocalDevAuth) {
      console.info("[Auth][native-dev] about to request access token", {
        url,
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      });
    }

    const tokenStartedAt = Date.now();
    const token = await getAccessTokenSilently({
      authorizationParams: {
        audience: import.meta.env.VITE_AUTH0_AUDIENCE,
        scope: "openid profile email",
      },
      timeoutInSeconds: TOKEN_TIMEOUT_SECONDS,
    });
    if (IS_NATIVE_APP || isLocalDevAuth) {
      console.info(
        `[Auth] token acquired in ${Date.now() - tokenStartedAt}ms for ${options.method || "GET"} ${url}`,
      );
    }

    // Don't set Content-Type for FormData - let browser set it automatically
    const isFormData = options.body instanceof FormData;

    // Defensive sanitization: if body is JSON string, remove explicit null
    // values for photo_url fields so backend zod validation doesn't reject them.
    if (options.body && !isFormData && typeof options.body === "string") {
      try {
        const parsed = JSON.parse(options.body as string);
        let changed = false;
        ["photo_url", "photo_url_2", "photo_url_3", "photo_url_4"].forEach(
          (k) => {
            if (
              Object.prototype.hasOwnProperty.call(parsed, k) &&
              parsed[k] === null
            ) {
              // Remove the key so JSON omits it (undefined vs null)
              delete parsed[k];
              changed = true;
            }
          },
        );
        if (changed) {
          options = { ...options, body: JSON.stringify(parsed) };
        }
      } catch (err) {
        // If parsing fails, leave body intact
      }
    }

    const authHeaders = {
      Authorization: `Bearer ${token}`,
      // Client-version headers so the backend can identify old store binaries.
      ...getClientHeaders(),
      // Only set Content-Type for non-FormData requests
      ...(options.body && !isFormData
        ? { "Content-Type": "application/json" }
        : {}),
      ...options.headers,
    };

    // Use environment variable for API base URL.
    const baseUrl =
      import.meta.env.VITE_REACT_APP_API_URL || "http://localhost:8080";
    const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;

    if (isLocalDevAuth) {
      console.info("[Auth][native-dev] token acquired, about to fetch", {
        fullUrl,
        method: options.method || "GET",
      });
    }

    // Guard the network request with a timeout so a dead connection fails fast
    // instead of hanging indefinitely. Respect a caller-provided signal if set.
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    const fetchStartedAt = Date.now();
    try {
      const response = await fetch(fullUrl, {
        ...options,
        headers: authHeaders,
        signal: options.signal ?? controller.signal,
      });
      if (IS_NATIVE_APP || isLocalDevAuth) {
        console.info(
          `[Auth] fetch completed in ${Date.now() - fetchStartedAt}ms (${response.status}) for ${options.method || "GET"} ${url}`,
        );
      }
      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  } catch (error) {
    if (isLocalDevAuth) {
      const e = error as
        | {
            error?: string;
            error_description?: string;
            message?: string;
          }
        | undefined;
      console.error(
        "[Auth][native-dev] authenticated request failed before/during fetch",
        {
          audience: import.meta.env.VITE_AUTH0_AUDIENCE,
          error: e?.error,
          error_description: e?.error_description,
          message: e?.message,
        },
      );
    }
    // Surface a clear, user-facing message for timeouts / no connectivity so
    // the UI doesn't just spin. Auth0 timeout -> "TimeoutError"/"timeout";
    // aborted fetch -> "AbortError"; offline fetch -> TypeError "Failed to fetch".
    const timeoutErr = error as {
      name?: string;
      error?: string;
      message?: string;
    };
    const isTimeoutOrNetwork =
      timeoutErr?.name === "AbortError" ||
      timeoutErr?.name === "TimeoutError" ||
      timeoutErr?.error === "timeout" ||
      (error instanceof TypeError &&
        /fetch|network/i.test(timeoutErr?.message || ""));
    if (isTimeoutOrNetwork) {
      console.error("Authenticated request timed out / no connection:", error);
      throw new NetworkTimeoutError();
    }
    console.error("Authenticated request failed:", error);
    throw error;
  }
}

/**
 * Hook for making API calls with user permissions
 */
export function useApiWithAuth() {
  const { getAccessTokenSilently, isAuthenticated, user, loginWithRedirect } = useAuth0();
  const isLocalDevAuth = String(
    import.meta.env.VITE_REACT_APP_API_URL || "",
  ).includes("localhost");
  const hasDevPermissionPrefix = usesDevPermissionPrefix();

  // Fallback: some Auth0 setups include permissions only in the access token (not in the ID token `user`).
  // Try to silently get an access token and decode permissions so we can rely on them even when
  // `user.permissions` is undefined.
  const [tokenPermissions, setTokenPermissions] = useState<
    string[] | undefined
  >(undefined);
  const [tokenRoles, setTokenRoles] = useState<string[] | undefined>(undefined);
  // True while the async access-token decode is in flight.
  // Starts true whenever the user is authenticated so consumers can gate UI on it.
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(
    () => isAuthenticated,
  );

  // Environment-aware permission prefix: "dev:kennel:" in dev, "kennel:" in prod
  const KENNEL_PERM_PREFIX = hasDevPermissionPrefix ? "dev:kennel:" : "kennel:"
  const KENNEL_ROLE_PREFIX = KENNEL_PERM_PREFIX
  // Environment-aware advertiser permission
  const ADVERTISER_PERM = hasDevPermissionPrefix ? "dev:advertiser:animals" : "advertiser:animals"
  const QA_ACCESS_PERM = hasDevPermissionPrefix ? "dev:qa:access" : "qa:access"
  // Resolved kennel ids were previously resolved in the hook; resolution now
  // happens only in admin components when needed to keep the global auth hook
  // lean and avoid public lookups.

  // Use a ref for getAccessTokenSilently to avoid re-running the effect when
  // Auth0 SDK returns a new function reference on each render.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  // Prevent the token decode effect from re-running after it has already resolved.
  const tokenDecodedRef = useRef(false);

  useEffect(() => {
    let mounted = true;
    if (!isAuthenticated) {
      setTokenPermissions(undefined);
      setIsPermissionsLoading(false);
      tokenDecodedRef.current = false;
      return;
    }
    // Only decode once per authentication session.
    if (tokenDecodedRef.current) return;

    setIsPermissionsLoading(true);

    (async () => {
      try {
        // Force a fresh access token (bypass the SDK cache) so newly-granted
        // Auth0 roles/permissions — e.g. the "advertiser" role assigned when a
        // Particular application is approved — are picked up on the next app
        // load instead of requiring a full logout/login. Use the SAME scope the
        // API calls use (see makeAuthenticatedRequest) so the refreshed token
        // lands in the cache entry those calls reuse, fixing both the frontend
        // gating and the backend permission checks. Fall back to the cached
        // token if the silent refresh fails (e.g. no valid refresh token).
        const tokenParams = {
          authorizationParams: {
            audience: import.meta.env.VITE_AUTH0_AUDIENCE,
            scope: "openid profile email",
          },
        };
        let token: string | undefined;
        try {
          token = await getTokenRef.current({
            ...tokenParams,
            cacheMode: "off",
          });
        } catch {
          token = await getTokenRef.current(tokenParams);
        }

        if (!token) {
          if (mounted) setIsPermissionsLoading(false);
          return;
        }
        const parts = token.split(".");
        if (parts.length < 2) {
          if (mounted) setIsPermissionsLoading(false);
          return;
        }
        const payload = JSON.parse(decodeURIComponent(escape(atob(parts[1]))));

        // Permissions may appear under different keys depending on Auth0 config.
        const perms =
          payload.permissions ||
          payload["https://pawseum.com/permissions"] ||
          payload.scope ||
          payload.scopes ||
          undefined;

        // Roles can appear in the token under different claims too.
        const rolesFromToken =
          payload["https://pawseum.com/roles"] || payload.roles || undefined;

        // Normalize to string[]
        const normalized = Array.isArray(perms)
          ? perms
          : typeof perms === "string"
            ? String(perms).split(" ")
            : undefined;

        if (mounted) {
          setTokenPermissions(normalized as string[] | undefined);

          const normalizedRoles = Array.isArray(rolesFromToken)
            ? rolesFromToken
            : typeof rolesFromToken === "string"
              ? String(rolesFromToken).split(" ")
              : undefined;

          setTokenRoles(normalizedRoles as string[] | undefined);
          setIsPermissionsLoading(false);
          tokenDecodedRef.current = true;
        }
      } catch (err) {
        // ignore: token might not contain permissions or user may not be allowed to get one
        if (mounted) {
          if (isLocalDevAuth) {
            const e = err as
              | {
                  error?: string;
                  error_description?: string;
                  message?: string;
                }
              | undefined;
            console.error("[Auth][native-dev] getAccessTokenSilently failed", {
              audience: import.meta.env.VITE_AUTH0_AUDIENCE,
              error: e?.error,
              error_description: e?.error_description,
              message: e?.message,
            });
          }
          setTokenPermissions(undefined);
          setIsPermissionsLoading(false);
        }
      }
    })();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // No-op: kennel resolution is done in admin forms (UnifiedAnimalForm), not here.

  const apiCallWithAuth = async (url: string, options: RequestInit = {}) => {
    if (!isAuthenticated) {
      throw new Error("User must be authenticated");
    }

    try {
      return await makeAuthenticatedRequest(url, options, getAccessTokenSilently);
    } catch (err: any) {
      if (err?.error === "login_required" || err?.error === "consent_required") {
        await loginWithRedirect({ appState: { returnTo: window.location.pathname } });
        throw err; // unreachable; browser navigates away
      }
      throw err;
    }
  };

  // Get user roles and permissions from Auth0
  // Prefer roles/permissions from decoded access token (tokenRoles/tokenPermissions),
  // then fall back to the `user` object.
  const userRoles =
    tokenRoles ?? user?.["https://pawseum.com/roles"] ?? user?.roles ?? [];
  const userPermissions = tokenPermissions ?? user?.permissions ?? [];

  // Determine admin/kennel permissions from explicit permissions (preferred)
  // Super admin can be provided either via explicit 'admin:all' permission OR 'admin' role (to mirror backend logic).
  const ADMIN_ALL_PERM = hasDevPermissionPrefix ? "dev:admin:all" : "admin:all"
  const ADMIN_PERM_PATTERN = hasDevPermissionPrefix
    ? /^(dev:)?admin(:|$)/i
    : /^admin(:|$)/i
  const hasAdminPermission = userPermissions.some((p: string) =>
    ADMIN_PERM_PATTERN.test(String(p)),
  );
  const hasAdminRole = (userRoles || []).some(
    (role: string) => String(role).toLowerCase() === "admin",
  );
  const isSuperAdmin =
    isAuthenticated &&
    (userPermissions.includes("admin:all") ||
      userPermissions.includes(ADMIN_ALL_PERM) ||
      hasAdminRole);

  // Kennel admin can be provided as a permission like `kennel-admin:ID` or
  // `kennel:ID` (prod) / `dev:kennel:ID` (dev). First check permissions
  // (preferred), then fall back to role-format if present.
  const kennelPerm = userPermissions.find((p: string) =>
    new RegExp(`^${KENNEL_PERM_PREFIX}|^kennel-admin:`, "i").test(String(p)),
  );
  const kennelRole = userRoles.find((role: string) =>
    new RegExp(`^${KENNEL_ROLE_PREFIX}|^kennel-admin:`, "i").test(String(role)),
  );
  const userKennelId = kennelPerm
    ? String(kennelPerm).split(":").pop()
    : kennelRole
      ? String(kennelRole).split(":").pop()
      : null;
  const isKennelAdmin = !!userKennelId;
  // console.log(userPermissions);

  // console.log("user: ", user, "is admin to:", userKennelId);

  // Check if user can manage animals (super admin or kennel admin)
  const canManageAnimals = isAuthenticated && (isSuperAdmin || isKennelAdmin);

  // Check if user can manage kennels (only super admin)
  const canManageKennels = isAuthenticated && isSuperAdmin;

  // Check if user is an approved Particular (private advertiser)
  const isAdvertiser =
    isAuthenticated &&
    (userPermissions.includes(ADVERTISER_PERM) ||
      userRoles.some((r: string) => r === "advertiser"));

  // Check if user can write blog posts (super admin or has blog:write permission/role)
  const canWriteBlog =
    isAuthenticated &&
    (isSuperAdmin ||
      userPermissions.includes("blog:write") ||
      userRoles.some((r: string) => r === "blog" || r === "blog:write"));

  // Check if user is any kind of admin
  // Any admin if they have any admin-related permission (admin:* or kennel:*) or kennel role
  const isAnyAdmin =
    isAuthenticated &&
    (isSuperAdmin || isKennelAdmin || hasAdminPermission || hasAdminRole);
  const hasQaAccess =
    isAuthenticated &&
    (isSuperAdmin || hasAdminPermission || userPermissions.includes(QA_ACCESS_PERM));

  // Determine available kennels
  const availableKennels = isSuperAdmin
    ? "all"
    : userKennelId
      ? [userKennelId]
      : [];

  // Admin API call with automatic kennel filtering
  const adminApiCall = async (url: string, options: RequestInit = {}) => {
    if (!isAnyAdmin) {
      throw new Error("Admin access required");
    }

    // Only apply kennel filtering for kennel admins (not super admins)
    // Super admins should see all animals from all kennels
    // Determine the kennel to apply for kennel admins (preference order):
    // 1. Explicit `kennel:ID` permission/role we parsed as `userKennelId`
    // 2. Kennel ID resolved from human-readable role(s)
    const kennelToApply = userKennelId;

    if (kennelToApply && !isSuperAdmin) {
      // Add kennel_id to request body for POST/PUT requests
      if (options.method === "POST" || options.method === "PUT") {
        // Skip JSON manipulation if body is FormData (multipart uploads)
        if (!(options.body instanceof FormData)) {
          const body = options.body ? JSON.parse(options.body as string) : {};
          body.kennel_id = kennelToApply;
          options.body = JSON.stringify(body);
        }
        // For FormData, kennel_id should already be appended in the calling code
      }

      // Add kennel filtering to GET requests
      if (!options.method || options.method === "GET") {
        const separator = url.includes("?") ? "&" : "?";
        url = `${url}${separator}kennelId=${kennelToApply}`;
      }
    }

    return apiCallWithAuth(url, options);
  };

  return {
    apiCallWithAuth, // Renamed from apiCall
    apiCall: apiCallWithAuth, // Backward compatibility
    adminApiCall, // New admin-specific call with auto-filtering
    isAuthenticated,
    user,
    userId: user?.sub,
    isSuperAdmin, // Super admin (all permissions)
    isAdmin: isSuperAdmin, // Backward compatibility
    canManageAnimals, // Can create/update/delete animals
    canManageKennels, // Can manage kennels
    canWriteBlog, // Can create/edit blog posts (draft only)
    isKennelAdmin: isKennelAdmin, // Kennel admin (specific kennel)
    isAnyAdmin, // Any admin type
    hasQaAccess, // QA environment access gate
    userKennelId, // Kennel ID for kennel admins
    availableKennels, // Available kennels based on permissions
    isPermissionsLoading, // True until the access-token decode has settled
    isAdvertiser, // Approved Particular (private advertiser)
  };
}
